/**
 * SessionRecorder — registra una línea de tiempo de todo lo que pasa durante
 * un caso (acciones, drogas, gestos, turnos de voz, snapshots de vitales).
 *
 * Es la materia prima del debrief cinematográfico con IA (Sprint 5): al
 * terminar el caso, la IA revisa la cronología y devuelve fortalezas,
 * omisiones críticas, y pearls educativas.
 *
 * Usa localStorage para sobrevivir un F5 accidental; una vez cerrado el
 * debrief o iniciado otro caso, se descarta.
 */

export type SessionEventKind =
  | 'case-start'
  | 'action'
  | 'drug'
  | 'gesture'
  | 'voice-student'
  | 'voice-patient'
  | 'narrative'
  | 'vitals-snapshot'
  | 'triage-change'
  | 'alarm';

export interface SessionEvent {
  t: number;                  // ms desde case-start
  at: number;                 // Date.now() absoluto
  kind: SessionEventKind;
  label: string;
  data?: Record<string, unknown>;
}

export interface SessionRecord {
  id: string;
  caseId: string | null;
  diagnosis: string | null;
  chiefComplaint: string | null;
  startedAt: number;
  endedAt: number | null;
  events: SessionEvent[];
}

const STORAGE_KEY = 'cgt_last_session';

export class SessionRecorder {
  private current: SessionRecord | null = null;

  start(caseId: string | null, diagnosis: string | null, chiefComplaint: string | null): void {
    this.current = {
      id: `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      caseId, diagnosis, chiefComplaint,
      startedAt: Date.now(),
      endedAt: null,
      events: [],
    };
    this.record('case-start', `Caso iniciado: ${chiefComplaint ?? caseId ?? 'desconocido'}`, {
      caseId, diagnosis,
    });
  }

  record(kind: SessionEventKind, label: string, data?: Record<string, unknown>): void {
    if (!this.current) return;
    const at = Date.now();
    this.current.events.push({
      t: at - this.current.startedAt,
      at, kind, label, data,
    });
    this.persist();
  }

  end(): SessionRecord | null {
    if (!this.current) return null;
    this.current.endedAt = Date.now();
    this.persist();
    const rec = this.current;
    return rec;
  }

  get(): SessionRecord | null {
    if (this.current) return this.current;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw) as SessionRecord;
    } catch { /* ignore */ }
    return null;
  }

  clear(): void {
    this.current = null;
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }

  /** Formatea la cronología en texto compacto para el prompt de la IA. */
  formatForAI(): string {
    if (!this.current) return '(sin eventos)';
    return this.current.events.map(ev => {
      const min = Math.floor(ev.t / 60000);
      const sec = Math.floor((ev.t % 60000) / 1000).toString().padStart(2, '0');
      return `[${min}:${sec}] ${ev.kind}: ${ev.label}`;
    }).join('\n');
  }

  /** Resumen numérico para badges. */
  summary(): { actions: number; drugs: number; voice: number; gestures: number; durationMin: number } {
    if (!this.current) return { actions: 0, drugs: 0, voice: 0, gestures: 0, durationMin: 0 };
    const evs = this.current.events;
    const dur = ((this.current.endedAt ?? Date.now()) - this.current.startedAt) / 60000;
    return {
      actions: evs.filter(e => e.kind === 'action').length,
      drugs: evs.filter(e => e.kind === 'drug').length,
      voice: evs.filter(e => e.kind === 'voice-student' || e.kind === 'voice-patient').length,
      gestures: evs.filter(e => e.kind === 'gesture').length,
      durationMin: Math.max(0, Math.round(dur * 10) / 10),
    };
  }

  private persist(): void {
    if (!this.current) return;
    try {
      // Cap para evitar exceder localStorage quota
      if (this.current.events.length > 500) {
        this.current.events = this.current.events.slice(-500);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.current));
    } catch { /* ignore */ }
  }
}

export const sessionRecorder = new SessionRecorder();
